import { useMemo, useState } from 'react'
import { Alert, Card, Progress, Segmented, Skeleton, Statistic, Table, Row, Col } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { BarChartOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { reportWindow } from '@/shared/lib/dayRange'
import { translateError } from '@/shared/api/errors'
import { usePermissions } from '@/shared/auth/usePermissions'
import { formatBytes } from '@/shared/lib/format'
import { EmptyState } from '@/shared/ui/EmptyState'
import { fetchSubscriberTrafficReport, type TrafficPoint } from './reportsApi'

interface SubscriberTrafficPanelProps {
  subscriberId: string
}

/** Oyna variantlari — serverdagi 366 kunlik chegaradan ancha ichkarida */
const WINDOWS = [7, 30, 90] as const
type WindowDays = (typeof WINDOWS)[number]

/**
 * Abonentning kunlik trafigi — `GET /api/reports/traffic/{subscriberId}`.
 *
 * <h2>⚠️ Manba `radacct` EMAS, `subscriber.usage_record`</h2>
 * Sessiyalar tarixi (`SessionHistoryTable`) va bu panel BIR XIL sonni
 * ko'rsatishi SHART emas va bu xato emas: sessiya hisoblagichi yarim
 * tundan o'tgan ulanishni bitta kunga to'liq yozadi, kunlik yozuv esa
 * uni tenant zonasidagi kun chegarasi bo'yicha bo'ladi
 * ({@code UsageResource} javadoc'i). Ikkalasi yonma-yon turgani shuning
 * uchun to'g'ri — biri "qachon ulandi", ikkinchisi "qaysi kunga yozildi".
 *
 * <h2>⚠️ Doiradan tashqarida — BO'SH ro'yxat, 403 emas</h2>
 * Backend ataylab shunday qaytaradi, ya'ni panel "xato" ko'rsatmasligi
 * kerak. Bo'sh holat "hali trafik yo'q" deb o'qiladi va bu to'g'ri.
 *
 * <h2>Nega grafik kutubxonasi yo'q</h2>
 * Admin panelning bog'liqliklarida chizish kutubxonasi yo'q va uni bitta
 * panel uchun qo'shish alohida qaror ({@code ReportsPage#ShareBar}).
 * Nisbatni ko'rsatishga jadval ichidagi chiziq yetarli.
 */
export function SubscriberTrafficPanel({ subscriberId }: SubscriberTrafficPanelProps) {
  const { t } = useTranslation()
  const { can } = usePermissions()
  const [days, setDays] = useState<WindowDays>(30)

  // ⚠️ Oyna TENANT kalendarida (`dayRange.ts`). Ilgari `dayjs()` — brauzer
  // kuni — olinardi, ya'ni yuqoridagi izohda yozilgan "tenant zonasidagi
  // kun chegarasi" AMALDA bajarilmasdi.
  const range = useMemo(() => reportWindow(days), [days])

  const allowed = can('report.read')

  const query = useQuery({
    queryKey: queryKeys.reports.subscriberTraffic(subscriberId, range.from, range.to, 'DAY'),
    queryFn: () => fetchSubscriberTrafficReport(subscriberId, range, 'DAY'),
    // ⚠️ Huquqsiz UMUMAN so'ramaydi. Aks holda har kartochka ochilganda
    // kafolatlangan 403 yuborilardi va audit jurnali shovqinga to'lardi.
    enabled: allowed,
    /*
      ⚠️ Manba KUNLIK yig'ma yozuv (`subscriber.usage_record`), ya'ni
      raqamlar kun chegarasida bir marta o'zgaradi. Serverda esa bu eng
      og'ir so'rovlardan biri — 90 kunlik oyna butun davr bo'yicha
      guruhlash demak. Umumiy 10 soniyalik chegara oyna almashtirilib
      qaytilganda uni bekorga qayta hisoblatardi.
    */
    staleTime: 5 * 60_000,
  })

  const rows = query.data ?? []
  const max = Math.max(0, ...rows.map((r) => r.totalBytes))
  const totals = rows.reduce(
    (acc, r) => ({
      upload: acc.upload + r.uploadBytes,
      download: acc.download + r.downloadBytes,
      total: acc.total + r.totalBytes,
      free: acc.free + r.freeBytes,
    }),
    { upload: 0, download: 0, total: 0, free: 0 },
  )

  const columns: ColumnsType<TrafficPoint> = [
    {
      key: 'label',
      // ⚠️ `label` SERVERDA tayyorlangan va TENANT zonasidagi kunni
      // bildiradi. `new Date(label)` bilan qayta formatlash kunni bir
      // kunga surib yuborardi (`reportsApi.ts`).
      title: t('reports.columns.period'),
      width: 130,
      render: (_, row) => row.label,
    },
    {
      key: 'upload',
      // ⚠️ "Yuklandi" — ABONENT yuborgan (upload). NAS nuqtai nazaridan
      // bu `acctinputoctets` va uni almashtirib qo'yish oson.
      title: t('reports.columns.upload'),
      width: 120,
      align: 'right',
      render: (_, row) => formatBytes(row.uploadBytes),
    },
    {
      key: 'download',
      title: t('reports.columns.download'),
      width: 120,
      align: 'right',
      render: (_, row) => formatBytes(row.downloadBytes),
    },
    {
      key: 'free',
      title: t('reports.columns.free'),
      width: 120,
      align: 'right',
      render: (_, row) => formatBytes(row.freeBytes),
    },
    {
      key: 'total',
      title: t('reports.columns.total'),
      render: (_, row) => (
        <div>
          <div>{formatBytes(row.totalBytes)}</div>
          {max > 0 ? (
            <Progress
              percent={Math.round((row.totalBytes / max) * 100)}
              showInfo={false}
              size="small"
              style={{ margin: 0, lineHeight: 1 }}
            />
          ) : null}
        </div>
      ),
    },
  ]

  const title = t('subscriber.trafficTitle')

  if (!allowed) {
    // Huquqi yo'q bo'lsa panel butunlay chizilmaydi: bo'sh karta
    // "ma'lumot yo'q" degan YOLG'ON xabar bo'lardi.
    return null
  }

  return (
    <Card
      title={title}
      extra={
        <Segmented<WindowDays>
          value={days}
          onChange={setDays}
          options={WINDOWS.map((d) => ({ value: d, label: t('subscriber.trafficDays', { count: d }) }))}
        />
      }
    >
      {query.isError ? (
        <Alert type="error" showIcon message={translateError(query.error, t)} />
      ) : query.isPending ? (
        <Skeleton active paragraph={{ rows: 5 }} />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<BarChartOutlined />}
          title={t('subscriber.trafficEmpty')}
          description={t('subscriber.trafficEmptyHint')}
        />
      ) : (
        <>
          <Row gutter={[16, 8]} style={{ marginBottom: 16 }}>
            <Col xs={12} md={6}>
              <Statistic title={t('reports.columns.upload')} value={formatBytes(totals.upload)} />
            </Col>
            <Col xs={12} md={6}>
              <Statistic title={t('reports.columns.download')} value={formatBytes(totals.download)} />
            </Col>
            <Col xs={12} md={6}>
              <Statistic title={t('reports.columns.total')} value={formatBytes(totals.total)} />
            </Col>
            <Col xs={12} md={6}>
              <Statistic title={t('reports.columns.free')} value={formatBytes(totals.free)} />
            </Col>
          </Row>
          <Table<TrafficPoint>
            columns={columns}
            dataSource={rows}
            rowKey={(row) => row.periodStart}
            size="small"
            pagination={rows.length > 15 ? { pageSize: 15, showSizeChanger: false } : false}
          />
        </>
      )}
    </Card>
  )
}
